import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Check, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RESET_PASSWORD } from "@/context/Api";

// TODO: replace with your real formatApiError import from utils if it exists elsewhere,
// e.g. import { formatApiError } from "@/lib/utils";
// This local fallback prevents a silent crash if that import was missing.
const formatApiError = (detail) => {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(", ");
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
};

// Same password policy shown during registration (RegistrationDialog.jsx).
const pwRules = (pw) => [
  { ok: pw.length >= 12, label: "At least 12 characters" },
  { ok: /[A-Z]/.test(pw), label: "One uppercase letter" },
  { ok: /[a-z]/.test(pw), label: "One lowercase letter" },
  { ok: /[0-9]/.test(pw), label: "One number" },
  { ok: /[@$%&*]/.test(pw), label: "One special character (@ $ % & *)" },
];

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const rules = useMemo(() => pwRules(password), [password]);
  const pwValid = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    

    if (!token) {
      console.warn("[ResetPassword] blocked: no token in URL");
      toast.error("This reset link is missing its token. Please request a new one.");
      return;
    }
    if (!pwValid) {
      console.warn("[ResetPassword] blocked: password rules not met", rules);
      toast.error("Please meet all password requirements.");
      return;
    }
    if (password !== confirm) {
      console.warn("[ResetPassword] blocked: passwords do not match");
      toast.error("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    const payload = { token, new_password: password };
    console.log("[ResetPassword] sending payload:", payload);

    try {
      const res = await RESET_PASSWORD(payload);
      console.log("[ResetPassword] success response:", res);
      setDone(true);
      toast.success("Password updated. You can now sign in.");
    } catch (err) {
      console.error("[ResetPassword] request failed:", err);
      toast.error(formatApiError(err?.response?.data?.detail ?? err?.detail) || "Could not reset your password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07070c] text-white font-body">
      <Nav />
      <div className="relative min-h-screen flex items-center justify-center px-6 noise overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full blur-[130px] opacity-40 bg-[#4d4479]" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="reset-password-card"
          className="relative z-10 w-full max-w-md rounded-2xl border border-[#3d414f] bg-[#0a0a12]/90 backdrop-blur-xl p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#16fcfb] text-[#07070c]"><ShieldCheck size={18} /></span>
            <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#16fcfb]">Reset Password</span>
          </div>
          <h1 className="font-display font-700 text-3xl text-white">Choose a new password.</h1>

          {!token && (
            <p className="mt-4 font-body text-sm text-[#97b8ff]">
              This link is missing its reset token. Please request a new one from the{" "}
              <Link to="/forgot-password" className="text-[#16fcfb] hover:underline">forgot password</Link> page.
            </p>
          )}

          {done ? (
            <div data-testid="reset-password-done" className="mt-8 flex flex-col items-center text-center">
              <ShieldCheck size={48} className="text-[#16fcfb]" />
              <p className="mt-5 font-body text-sm text-white/80 max-w-sm">Your password has been updated.</p>
              <Button
                data-testid="reset-password-go-to-login"
                onClick={() => navigate("/login")}
                className="mt-6 rounded-full bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest px-8"
              >
                Go to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-5" noValidate>
              <div className="space-y-2">
                <Label htmlFor="reset-password" className="font-mono2 text-[11px] uppercase tracking-[0.12em] text-[#97b8ff]">New password</Label>
                <div className="relative">
                  <Input
                    id="reset-password"
                    data-testid="reset-new-password"
                    type={showPw ? "text" : "password"}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] pr-10"
                  />
                  <button type="button" onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-1.5 pt-1" data-testid="reset-pw-rules">
                  {rules.map((r) => (
                    <div key={r.label} className="flex items-center gap-2 font-body text-xs">
                      {r.ok ? <Check size={13} className="text-[#16fcfb]" /> : <X size={13} className="text-white/30" />}
                      <span className={r.ok ? "text-white/80" : "text-white/40"}>{r.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reset-confirm-password" className="font-mono2 text-[11px] uppercase tracking-[0.12em] text-[#97b8ff]">Confirm password</Label>
                <Input id="reset-confirm-password" data-testid="reset-confirm-password" type={showPw ? "text" : "password"}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your new password"
                  className="bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb]" />
              </div>

              <Button type="submit" data-testid="reset-password-submit-btn" disabled={submitting || !token}
                className="w-full rounded-full h-12 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan">
                {submitting ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Updating</>) : "Update password"}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;